/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import type { UploadProgress as UploadProgressType } from '@/types/invoices.types'

interface UploadProgressProps {
  uploads: UploadProgressType[]
}

export function UploadProgress({ uploads }: UploadProgressProps) {
  if (uploads.length === 0) return null

  const getStatusIcon = (status: UploadProgressType['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-muted-foreground" />
      case 'uploading':
        return <Loader2 className="h-5 w-5 text-primary animate-spin" />
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />
      case 'error':
        return <XCircle className="h-5 w-5 text-destructive" />
    }
  }

  const getStatusText = (upload: UploadProgressType) => {
    switch (upload.status) {
      case 'pending':
        return 'Čeká na nahrání'
      case 'uploading':
        return `Nahrávání... ${upload.progress}%`
      case 'success':
        return 'Nahráno úspěšně'
      case 'error':
        return upload.error || 'Chyba při nahrávání'
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">Stav nahrávání</h3>
      <div className="space-y-3">
        {uploads.map((upload, index) => (
          <div
            key={`${upload.file.name}-${index}`}
            className="p-3 border rounded-lg space-y-2"
          >
            <div className="flex items-center gap-3">
              {getStatusIcon(upload.status)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {upload.file.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {getStatusText(upload)}
                </p>
              </div>
            </div>
            {upload.status === 'uploading' && (
              <Progress value={upload.progress} className="h-1" />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
